/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import LazyLoadImage from "web/Layout/Common/LazyLoadImage";
import Price from "web/Layout/Common/Price";
import __ from "web/Layout/Translations";

import ToolTip from "web/Components/Common/ToolTip";

import useIsPL from "web/hooks/useIsPL";
import useOnProductClikCallback from "web/hooks/useOnProductClikCallback";

import setBenefitGroupTypeParam from "web/utils/page/product/subscription/setBenefitGroupTypeParam";
import { isSubscriptionDisabled } from "web/utils/page/product/subscription/windowGroupConfig";
import processUrlKey from "web/utils/page/product/universal/processUrlKey";

import urls from "web/constants/urls";

import type { PropsWithClasses } from "web/types/Common";
import {
  DomesticTourismProduct,
  IProduct,
  MBProductType,
  ProductInList,
} from "web/types/Product";

import classify from "web/classify";

import type { ProductCartListProps } from "./container";
import defaultClasses from "./product.scss";

const ProductCardListProduct: FC<PropsWithClasses<ProductCartListProps>> = ({
  product,
  classes = {},
  positionNumber = 0,
}) => {
  const { open: isWindowOpen, openUms: isUmsWindowOpen } =
    useSelector((state) => state.subscriptionQuote.config) || {};
  const isPL = useIsPL();
  const isDisabled = useMemo(
    () =>
      isSubscriptionDisabled(
        (product as ProductInList).mb_product_type,
        isWindowOpen,
        isUmsWindowOpen
      ),
    [(product as ProductInList)?.mb_product_type, isWindowOpen, isUmsWindowOpen]
  );

  if (!product) {
    return null;
  }
  const {
    name,
    image,
    url_key: urlKey,
    final_price: finalPrice,
    price,
    mb_product_type: productType,
  } = product as IProduct;

  const dataSubscription = useSelector((state) => state.benefitGroups);
  const benefitGroups = dataSubscription?.benefitGroups ?? {};
  const isSubscription =
    typeof productType === "string" ? productType.includes("abonament") : false;

  const hasAnixePrice =
    productType === MBProductType.ANIXE_TOURISM &&
    (product as IProduct<DomesticTourismProduct>).tourism_anixe_min_offer
      .total_price;
  const hasDiscount = finalPrice < price;
  const hasConfigurablePrice = !!(product as IProduct)?.product_options
    ?.configurable_options?.price;

  const [sendProductClickEvent] = useOnProductClikCallback(
    product as IProduct,
    positionNumber
  );

  const fromPrice = hasAnixePrice || hasConfigurablePrice;

  const itemName = name?.length > 100 ? `${name.substring(0, 100)}...` : name;

  const to = useMemo(() => {
    if (isDisabled) {
      return null;
    }
    return isSubscription
      ? {
          pathname: urls.checkoutSubscriptionConfiguration,
          search: `${setBenefitGroupTypeParam(
            (product as ProductInList).mb_product_type.split("_")[2]
          )}&id=${(product as ProductInList).sku}`,
        }
      : processUrlKey(urlKey);
  }, [isSubscription, product, urlKey]);

  const subscriptionPrice = isSubscription
    ? benefitGroups[(product as IProduct).sku]?.price
    : null;

  return (
    <Link
      // @ts-ignore
      to={to}
      className={`${classes.root} ${isDisabled ? classes.disabled : ""}`}
      onClick={() => sendProductClickEvent()}
    >
      <div className={classes.leftSideContainer}>
        <div className={classes.imageContainer}>
          <LazyLoadImage
            className={classes.image}
            src={image}
            alt=""
            isProduct
          />
        </div>
      </div>
      <div className={classes.wrapper}>
        <h3
          className={isSubscription ? classes.nameSubscription : classes.name}
          data-t1="category_mobile_simple_product_name"
        >
          {itemName}
        </h3>
        {hasAnixePrice || finalPrice || subscriptionPrice ? (
          <>
            {hasDiscount && (
              <div className={classes.priceBeforeDiscount}>
                <Price value={price} currency={__("zł")} />
              </div>
            )}
            <div className={classes.price}>
              {isDisabled && (
                <span className={classes.tooltip}>
                  <ToolTip message={__("Minął termin wyboru abonamentów")} />
                </span>
              )}
              <div
                className={`${
                  hasDiscount
                    ? classes.priceWrapperWithDiscount
                    : classes.priceWrapper
                } ${isPL ? "" : classes.en}`}
              >
                <Price
                  from={fromPrice as boolean}
                  value={
                    hasAnixePrice
                      ? (product as IProduct<DomesticTourismProduct>)
                          .tourism_anixe_min_offer.total_price
                      : subscriptionPrice || finalPrice
                  }
                  currency={__("zł")}
                />
              </div>
            </div>
          </>
        ) : null}
        {isSubscription && !subscriptionPrice && (
          <div className={classes.price}>
            {isDisabled && (
              <span className={classes.tooltip}>
                <ToolTip message={__("Minął termin wyboru abonamentów")} />
              </span>
            )}
            <div
              className={
                isSubscription
                  ? classes.checkSubscriptions
                  : classes.priceWrapper
              }
            >
              {__("Sprawdź")}
            </div>
          </div>
        )}
      </div>
    </Link>
  );
};

export default classify<PropsWithClasses<ProductCartListProps>>(defaultClasses)(
  ProductCardListProduct
);
